import clsx from 'clsx'
import type { ReactNode } from 'react'
import { View } from 'react-native'

import { Image } from '@/src/elements/image'
import { Placeholder, PlaceholderMedia } from '@/src/elements/placeholder'
import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { getImageUrl } from '@/src/general/utils/getImageUrl'

import { useTagQuery } from '../../../../tag/services/useTagQuery'

export type TCommunitiesCategoryTagImageProps = {
  tag?: string
  ImageComponent?: () => ReactNode
  customTagName?: { text: string | ReactNode; className: string }
}

export const CommunitiesCategoryTagImage = ({
  tag: tagParam,
  ImageComponent,
  customTagName,
}: TCommunitiesCategoryTagImageProps): ReactNode => {
  const { data: tag, isPending } = useTagQuery(tagParam || '', !!tagParam)
  const { sm, xm, md, lg, xl, lessThan } = useDimensions()

  const getContentPosition = () => {
    if (xl || lg) {
      return { top: '40%' }
    } else if (xm || md || sm) {
      return { right: 0, top: '30%' }
    }
    return undefined
  }

  const getTextSize = () => {
    if (lg || xl) return '3xl'
    if (md || xm) return '2xl'
    return 'xl'
  }

  return (
    <View testID={TEST_ID.MISCELLANEOUS.TAG_IMAGE} className="h-[280px] w-full rounded-none xm:h-[300px] lg:h-[360px]">
      {tag && !ImageComponent && (
        <Image
          source={{ uri: lessThan.lg ? getImageUrl(tag.icon) : getImageUrl(tag.picture) }}
          className="h-full w-auto rounded-none lg:max-h-full"
          contentFit="cover"
          contentPosition={getContentPosition()}
          placeholder={{ blurhash: 'LmLg9W-oNGt7~Cs.ofWC4:RkfRR*', isAnimated: true }}
        />
      )}
      {tagParam && isPending && !ImageComponent && (
        <Placeholder className="h-full w-full items-center">
          <PlaceholderMedia contrast="dark" className="h-5/6 w-full rounded-md" />
        </Placeholder>
      )}
      {ImageComponent && !tag && <ImageComponent />}
      <Typography
        size={getTextSize()}
        black
        className={clsx(
          'absolute bottom-0 left-0 right-0 mx-20 mb-12 bg-transparent text-center text-white',
          customTagName?.className,
        )}
      >
        {customTagName?.text || tag?.name}
      </Typography>
    </View>
  )
}
